/**
 * GHG Word export (html-docx-js): Word-safe HTML — nested tables, inline styles,
 * bgcolor + mso-* fallbacks. Rounded UI uses canvas PNGs (Word ignores border-radius/VML).
 */

export const GHG_WORD_EXPORT_DESIGN_TOKENS = {
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
  primaryBlue: '#1d4ed8',
  accentYellow: '#f59e0b',
  coverGhgLabelBadgeRadius: '12pt',
  coverGhgLabelPaddingY: '10pt',
  coverGhgLabelPaddingX: '28pt',
  tocLabelBadgeRadius: '8pt',
  tocLabelPaddingY: '10pt',
  tocLabelPaddingX: '24pt',
  tocBadgeSize: '44pt',
  tocBadgeRadius: '8pt',
  /** A4 portrait page (210mm) — match GHG cover card width in Word. */
  tocPageWidth: '8.27in',
  /** Full A4 height — cover only; TOC uses tocContentLayoutMinHeight on inner layout table. */
  tocPageMinHeight: '11.69in',
  /** Fills card below title so footer accent bar sits near page bottom (Word table spacer). */
  tocContentLayoutMinHeight: '8.35in',
  /** Gap between last TOC row and flexible spacer / footer block. */
  tocFooterContentGap: '20pt',
  /** Pushes footer bar toward bottom inside layout table (html-docx-js respects fixed row height). */
  tocFooterSpacerHeight: '96pt',
  tocShellPaddingWord: '54pt 48pt 44pt 48pt',
  tocCardBorder: '#e5e7eb',
  tocCardRadius: '12pt',
  /** Fixed pt columns for html-docx-js (percent cols overlap badges on A4). */
  tocBadgeColWidth: '44pt',
  tocTextColWidth: '191pt',
  tocGridColGutter: '12pt',
  tocBadgeTextGap: '11pt',
  tocItemRowPaddingBottom: '14pt',
  tocRowDivider: '#e2e8f0',
  coverMetaIconBoxSizePt: '34pt',
  coverMetaIconRadiusPt: '6pt',
  coverMetaIconInnerPt: '18pt',
  /** Company logo on GHG cover — Word export only (browser HTML unchanged). */
  coverLogoHeightPt: '54pt',
  coverLogoMaxWidthPt: '120pt',
  sectionHeadBarBg: '#4f46e5',
  sectionHeadBarTitleColor: '#ffffff',
  /** Saved GHG report section titles (e.g. GHG Emissions Inventory) — orange pill, flat left. */
  sectionHeadPillDefaultBg: '#fd7e14',
  sectionHeadPillRadiusRight: '30pt',
  sectionHeadPillPadTop: '12pt',
  sectionHeadPillPadRight: '32pt',
  sectionHeadPillPadBottom: '12pt',
  sectionHeadPillPadLeft: '22pt',
  sectionHeadPillFontSize: '18pt',
  /** Extra scale for Word PNG display (html-docx-js renders pills smaller than browser). */
  sectionHeadPillWordScale: 1.2,
  /** Live GHGDataView .ghg-section-head-bar (h5) in Word */
  sectionHeadBarPillFontSize: '20pt',
  sectionHeadBarPillPadTop: '10pt',
  sectionHeadBarPillPadRight: '26pt',
  sectionHeadBarPillPadBottom: '10pt',
  sectionHeadBarPillPadLeft: '16pt',
  sectionHeadBarPillRadiusRight: '26pt',
  darkHighlightBg: '#3e670f',
};

const T = GHG_WORD_EXPORT_DESIGN_TOKENS;

/** Inline style for cover company logo (Word export prep). */
export const getGhgCoverLogoWordInlineStyle = () =>
  `height:${T.coverLogoHeightPt};max-height:${T.coverLogoHeightPt};width:auto;max-width:${T.coverLogoMaxWidthPt};display:inline-block;border:0;mso-line-height-rule:exactly;`;

const PX_PER_PT = 96 / 72;

const parsePtToPx = (ptValue) => {
  const n = parseFloat(String(ptValue || '0'));
  return Number.isFinite(n) ? n * PX_PER_PT : 0;
};

const scalePtValue = (ptValue, scale) => {
  const n = parseFloat(String(ptValue || '0'));
  if (!Number.isFinite(n) || n <= 0) return ptValue;
  const s = Number(scale) > 0 ? Number(scale) : 1;
  return `${Math.round(n * s * 10) / 10}pt`;
};

const applySectionHeadPillWordScale = (options = {}) => {
  const scale = Number(options.wordScale ?? T.sectionHeadPillWordScale) || 1;
  if (scale === 1) return options;
  return {
    ...options,
    fontSizePt: scalePtValue(options.fontSizePt || T.sectionHeadPillFontSize, scale),
    padTopPt: scalePtValue(options.padTopPt || T.sectionHeadPillPadTop, scale),
    padRightPt: scalePtValue(options.padRightPt || T.sectionHeadPillPadRight, scale),
    padBottomPt: scalePtValue(options.padBottomPt || T.sectionHeadPillPadBottom, scale),
    padLeftPt: scalePtValue(options.padLeftPt || T.sectionHeadPillPadLeft, scale),
    radiusRight: scalePtValue(options.radiusRight || T.sectionHeadPillRadiusRight, scale),
  };
};

export const escapeHtml = (value) => {
  if (value == null || value === '') return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const labelPlainFromEscaped = (labelEscaped) => {
  if (labelEscaped == null) return '';
  if (typeof document === 'undefined') {
    return String(labelEscaped)
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .trim();
  }
  const d = document.createElement('div');
  d.innerHTML = String(labelEscaped);
  return (d.textContent || '').trim();
};

const drawRoundRectPath = (ctx, x, y, w, h, radiusPx) => {
  const r = Math.max(0, Math.min(radiusPx, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

/** Flat left edge, rounded right end (GHG report section title bar). */
const drawSectionHeadPillRightRoundPath = (ctx, x, y, w, h, radiusPx) => {
  const r = Math.max(0, Math.min(radiusPx, h / 2));
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x, y + h);
  ctx.closePath();
};

const mergeInlineStyle = (el, addition) => {
  if (!el?.setAttribute) return;
  let cur = el.getAttribute('style') || '';
  cur = cur.replace(/\bbackground-color\s*:\s*[^;]+;?/gi, '').trim();
  const next = cur ? (cur.endsWith(';') ? `${cur} ${addition}` : `${cur}; ${addition}`) : addition;
  el.setAttribute('style', next);
};

const buildGhgLabelBadgeTableHtml = (labelEscaped, bgColor, variant) => {
  const isCover = variant === 'cover';
  const radius = isCover ? T.coverGhgLabelBadgeRadius : T.tocLabelBadgeRadius;
  const padY = isCover ? T.coverGhgLabelPaddingY : T.tocLabelPaddingY;
  const padX = isCover ? T.coverGhgLabelPaddingX : T.tocLabelPaddingX;
  const className = isCover ? 'ghg-cover-label-badge' : 'ghg-toc-label-badge';
  const marginBottom = isCover ? '8pt' : '14pt';

  return `<table class="${className}" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;border:none;mso-border-alt:none;margin:0 0 ${marginBottom} 0;">
<tr><td bgcolor="${bgColor}" style="background-color:${bgColor};color:#ffffff;padding:${padY} ${padX};mso-padding-alt:${padY} ${padX};border-radius:${radius};border:none;mso-border-alt:none;mso-line-height-rule:exactly;">
<span style="color:#ffffff;font-size:10pt;font-weight:800;letter-spacing:1.5pt;font-family:'Inter',Arial,sans-serif;mso-line-height-rule:exactly;line-height:1.2;">${labelEscaped}</span>
</td></tr></table>`;
};

/**
 * Renders the blue GHG pill as PNG — html-docx-js ignores CSS border-radius and VML.
 */
export function renderGhgLabelBadgeAsImageHtml(
  labelPlain,
  labelEscaped,
  bgColor = T.primaryBlue,
  variant = 'cover',
) {
  const plain = (labelPlain || '').trim();
  const escaped = labelEscaped != null ? labelEscaped : escapeHtml(plain);
  if (!plain) return '';

  const isCover = variant === 'cover';
  const padY = parsePtToPx(isCover ? T.coverGhgLabelPaddingY : T.tocLabelPaddingY);
  const padX = parsePtToPx(isCover ? T.coverGhgLabelPaddingX : T.tocLabelPaddingX);
  const radius = parsePtToPx(isCover ? T.coverGhgLabelBadgeRadius : T.tocLabelBadgeRadius);
  const marginBottomPt = isCover ? 8 : 14;
  const fontSizePx = parsePtToPx('10pt');
  const font = `800 ${fontSizePx}px Arial, Helvetica, sans-serif`;
  const className = isCover ? 'ghg-cover-label-badge-img' : 'ghg-toc-label-badge-img';

  if (typeof document === 'undefined') {
    return buildGhgLabelBadgeTableHtml(escaped, bgColor, variant);
  }

  try {
    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    if (!measureCtx) return buildGhgLabelBadgeTableHtml(escaped, bgColor, variant);

    measureCtx.font = font;
    const textWidth = measureCtx.measureText(plain).width;
    const cssW = Math.ceil(textWidth + padX * 2);
    const cssH = Math.ceil(fontSizePx + padY * 2);

    const canvas = document.createElement('canvas');
    const scale = 4;
    canvas.width = Math.max(1, cssW * scale);
    canvas.height = Math.max(1, cssH * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return buildGhgLabelBadgeTableHtml(escaped, bgColor, variant);

    ctx.scale(scale, scale);
    drawRoundRectPath(ctx, 0, 0, cssW, cssH, radius);
    ctx.fillStyle = bgColor;
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(plain, cssW / 2, cssH / 2);

    const dataUrl = canvas.toDataURL('image/png');
    const widthPt = (cssW * 72) / 96;
    const heightPt = (cssH * 72) / 96;

    return `<span class="${className}" style="display:inline-block;margin:0 0 ${marginBottomPt}pt 0;line-height:0;mso-line-height-rule:exactly;font-size:0;"><img src="${dataUrl}" alt="${escaped}" width="${cssW}" height="${cssH}" style="width:${widthPt}pt;height:${heightPt}pt;display:block;border:0;margin:0;padding:0;" /></span>`;
  } catch {
    return buildGhgLabelBadgeTableHtml(escaped, bgColor, variant);
  }
}

export function buildGhgLabelBadgeHtml(labelEscaped, bgColor = T.primaryBlue, variant = 'toc') {
  const plain = labelPlainFromEscaped(labelEscaped);
  return renderGhgLabelBadgeAsImageHtml(plain, labelEscaped, bgColor, variant);
}

const buildGhgSectionHeadPillTableFallback = (titleEscaped, bgColor, options = {}) => {
  const pad = options.paddingCss
    || `${T.sectionHeadPillPadTop} ${T.sectionHeadPillPadRight} ${T.sectionHeadPillPadBottom} ${T.sectionHeadPillPadLeft}`;
  const radius = options.radiusRight || T.sectionHeadPillRadiusRight;
  const fontSize = options.fontSizePt || T.sectionHeadPillFontSize;
  return `<table class="ghg-section-head-pill-fallback" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;border:none;mso-border-alt:none;margin:0 0 18pt 0;display:inline-block;">
<tr><td bgcolor="${bgColor}" style="background-color:${bgColor};color:#ffffff;padding:${pad};mso-padding-alt:${pad};border-radius:0 ${radius} ${radius} 0;border:none;mso-border-alt:none;mso-line-height-rule:exactly;">
<span style="color:#ffffff;font-size:${fontSize};font-weight:700;font-family:'Inter',Arial,sans-serif;mso-line-height-rule:exactly;white-space:nowrap;">${titleEscaped}</span>
</td></tr></table>`;
};

/**
 * Section title pill (flat left, rounded right) as PNG — Word ignores border-radius on divs.
 * @param {string} titlePlain
 * @param {string} [bgColor]
 * @param {{ fontSizePt?: string, paddingCss?: string, radiusRight?: string, marginBottomPt?: number }} [options]
 */
export function renderGhgSectionHeadPillAsImageHtml(
  titlePlain,
  bgColor = T.sectionHeadPillDefaultBg,
  options = {},
) {
  const plain = (titlePlain || '').replace(/\s+/g, ' ').trim();
  const escaped = escapeHtml(plain);
  if (!plain) return '';

  const scaled = applySectionHeadPillWordScale(options);
  const padTop = parsePtToPx(scaled.padTopPt || T.sectionHeadPillPadTop);
  const padRight = parsePtToPx(scaled.padRightPt || T.sectionHeadPillPadRight);
  const padBottom = parsePtToPx(scaled.padBottomPt || T.sectionHeadPillPadBottom);
  const padLeft = parsePtToPx(scaled.padLeftPt || T.sectionHeadPillPadLeft);
  const radius = parsePtToPx(scaled.radiusRight || T.sectionHeadPillRadiusRight);
  const fontSizePx = parsePtToPx(scaled.fontSizePt || T.sectionHeadPillFontSize);
  const marginBottomPt = options.marginBottomPt != null ? options.marginBottomPt : 18;
  const font = `700 ${fontSizePx}px Arial, Helvetica, sans-serif`;

  if (typeof document === 'undefined') {
    return buildGhgSectionHeadPillTableFallback(escaped, bgColor, options);
  }

  try {
    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    if (!measureCtx) return buildGhgSectionHeadPillTableFallback(escaped, bgColor, options);

    measureCtx.font = font;
    const textWidth = measureCtx.measureText(plain).width;
    const cssW = Math.ceil(textWidth + padLeft + padRight);
    const cssH = Math.ceil(fontSizePx * 1.15 + padTop + padBottom);

    const canvas = document.createElement('canvas');
    const scale = 4;
    canvas.width = Math.max(1, cssW * scale);
    canvas.height = Math.max(1, cssH * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return buildGhgSectionHeadPillTableFallback(escaped, bgColor, options);

    ctx.scale(scale, scale);
    drawSectionHeadPillRightRoundPath(ctx, 0, 0, cssW, cssH, radius);
    ctx.fillStyle = bgColor;
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = font;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(plain, padLeft, cssH / 2);

    const dataUrl = canvas.toDataURL('image/png');
    const widthPt = (cssW * 72) / 96;
    const heightPt = (cssH * 72) / 96;

    const widthPtRounded = Math.round(widthPt * 10) / 10;
    const heightPtRounded = Math.round(heightPt * 10) / 10;
    return `<span class="ghg-section-head-pill-img" style="display:inline-block;margin:0 0 ${marginBottomPt}pt 0;line-height:0;mso-line-height-rule:exactly;font-size:0;"><img class="ghg-section-head-pill-img" src="${dataUrl}" alt="${escaped}" width="${widthPtRounded}" height="${heightPtRounded}" style="width:${widthPtRounded}pt !important;height:${heightPtRounded}pt !important;max-width:none !important;display:inline-block;border:0;margin:0;padding:0;mso-line-height-rule:exactly;" /></span>`;
  } catch {
    return buildGhgSectionHeadPillTableFallback(escaped, bgColor, options);
  }
}

const parseInlineBackgroundColor = (style) => {
  const m = String(style || '').match(/background-color:\s*([^;]+)/i);
  return m?.[1]?.trim() || '';
};

const parseInlinePt = (style, prop) => {
  const re = new RegExp(`${prop}\\s*:\\s*([\\d.]+)\\s*pt`, 'i');
  const m = String(style || '').match(re);
  return m?.[1] ? `${m[1]}pt` : '';
};

/** padding: top right bottom left (pt) from inline style */
const parseInlinePaddingPt = (style) => {
  const m = String(style || '').match(/padding:\s*([^;]+)/i);
  if (!m) return {};
  const parts = m[1].trim().split(/\s+/).filter(Boolean);
  const toPt = (v) => (/\d/.test(v) && !/pt$/i.test(v) ? `${parseFloat(v)}pt` : v);
  if (parts.length === 1) {
    const p = toPt(parts[0]);
    return { padTopPt: p, padRightPt: p, padBottomPt: p, padLeftPt: p };
  }
  if (parts.length === 2) {
    const a = toPt(parts[0]);
    const b = toPt(parts[1]);
    return { padTopPt: a, padRightPt: b, padBottomPt: a, padLeftPt: b };
  }
  if (parts.length >= 4) {
    return {
      padTopPt: toPt(parts[0]),
      padRightPt: toPt(parts[1]),
      padBottomPt: toPt(parts[2]),
      padLeftPt: toPt(parts[3]),
    };
  }
  return {};
};

const buildSectionHeadPillOptionsFromElement = (div) => {
  const divStyle = div.getAttribute('style') || '';
  const h3 = div.querySelector(':scope > h3');
  const h3Style = h3?.getAttribute('style') || '';
  const padding = parseInlinePaddingPt(divStyle);
  const fontSizePt = parseInlinePt(h3Style, 'font-size') || parseInlinePt(divStyle, 'font-size');
  const radiusMatch = divStyle.match(/border-radius:\s*([^;]+)/i);
  let radiusRight = T.sectionHeadPillRadiusRight;
  if (radiusMatch) {
    const corners = radiusMatch[1].trim().split(/\s+/);
    const rightVal = corners.length >= 2 ? corners[1] : corners[0];
    if (rightVal && /\d/.test(rightVal)) {
      radiusRight = /pt$/i.test(rightVal) ? rightVal : `${parseFloat(rightVal)}pt`;
    }
  }
  return {
    ...padding,
    ...(fontSizePt ? { fontSizePt } : {}),
    radiusRight,
  };
};

const isGhgInlineSectionHeadPill = (el) => {
  if (!el || String(el.tagName).toLowerCase() !== 'div') return false;
  if (el.classList.contains('ghg-section-head-pill-img')) return false;
  if (el.querySelector('img.ghg-section-head-pill-img')) return false;
  const style = (el.getAttribute('style') || '').toLowerCase();
  if (!/display\s*:\s*inline-block/.test(style)) return false;
  const bg = parseInlineBackgroundColor(style);
  if (!bg) return false;
  const h3 = el.querySelector(':scope > h3');
  if (!h3) return false;
  return true;
};

function upgradeGhgInlineSectionHeadPillsForWord(root) {
  const pills = [...root.querySelectorAll('div')].filter(isGhgInlineSectionHeadPill);
  pills.forEach((div) => {
    const style = div.getAttribute('style') || '';
    const bg = parseInlineBackgroundColor(style) || T.sectionHeadPillDefaultBg;
    const h3 = div.querySelector(':scope > h3');
    const titlePlain = (h3?.textContent || '').replace(/\s+/g, ' ').trim();
    if (!titlePlain) return;

    const pillHtml = renderGhgSectionHeadPillAsImageHtml(titlePlain, bg, {
      ...buildSectionHeadPillOptionsFromElement(div),
      wordScale: T.sectionHeadPillWordScale,
    });
    const doc = div.ownerDocument;
    const temp = doc.createElement('div');
    temp.innerHTML = pillHtml;
    const pill = temp.firstElementChild;
    if (!pill) return;
    div.replaceWith(pill);
  });
}

const META_ICON_PATHS = {
  doc: 'M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z',
  user: 'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
  calendar:
    'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z',
};

const drawMetaIcon = (ctx, iconType, cx, cy, sizePx) => {
  const pathStr = META_ICON_PATHS[iconType];
  if (!pathStr || !ctx) return;
  const scale = sizePx / 24;
  ctx.save();
  ctx.translate(cx - sizePx / 2, cy - sizePx / 2);
  ctx.scale(scale, scale);
  try {
    const path = new Path2D(pathStr);
    ctx.fillStyle = '#ffffff';
    ctx.fill(path);
  } catch {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(12, 12, 8, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
};

/**
 * Rounded meta icon box (document / user / calendar) as PNG for Word.
 * @param {'doc'|'user'|'calendar'} iconType
 */
export function buildGhgCoverMetaIconBoxHtml(iconType, bgColor = T.primaryBlue) {
  const boxPx = parsePtToPx(T.coverMetaIconBoxSizePt);
  const radiusPx = parsePtToPx(T.coverMetaIconRadiusPt);
  const iconPx = parsePtToPx(T.coverMetaIconInnerPt);
  const cssW = Math.ceil(boxPx);
  const cssH = Math.ceil(boxPx);

  if (typeof document === 'undefined') {
    return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr><td bgcolor="${bgColor}" style="width:${T.coverMetaIconBoxSizePt};height:${T.coverMetaIconBoxSizePt};background-color:${bgColor};text-align:center;vertical-align:middle;">&#8203;</td></tr></table>`;
  }

  try {
    const canvas = document.createElement('canvas');
    const scale = 4;
    canvas.width = Math.max(1, cssW * scale);
    canvas.height = Math.max(1, cssH * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return `<table cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="${bgColor}" style="background-color:${bgColor};width:${T.coverMetaIconBoxSizePt};height:${T.coverMetaIconBoxSizePt};">&#8203;</td></tr></table>`;
    }

    ctx.scale(scale, scale);
    drawRoundRectPath(ctx, 0, 0, cssW, cssH, radiusPx);
    ctx.fillStyle = bgColor;
    ctx.fill();
    drawMetaIcon(ctx, iconType, cssW / 2, cssH / 2, iconPx);

    const dataUrl = canvas.toDataURL('image/png');
    const widthPt = (cssW * 72) / 96;
    const heightPt = (cssH * 72) / 96;

    return `<img class="ghg-cover-meta-icon-img" data-ghg-meta-icon="${iconType}" src="${dataUrl}" alt="" width="${cssW}" height="${cssH}" style="display:block;width:${widthPt}pt;border:0;margin:0;padding:0;" />`;
  } catch {
    return `<table cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="${bgColor}" style="background-color:${bgColor};width:${T.coverMetaIconBoxSizePt};height:${T.coverMetaIconBoxSizePt};">&#8203;</td></tr></table>`;
  }
}

const buildGhgTocNumberBadgeTableHtml = (numStr, bgColor) => {
  const size = T.tocBadgeSize;
  return `<table class="ghg-toc-badge-table" width="44" height="44" cellpadding="0" cellspacing="0" border="0" style="width:${size} !important;max-width:${size} !important;height:${size} !important;border-collapse:collapse;border:none;mso-border-alt:none;mso-table-lspace:0pt;mso-table-rspace:0pt;"><tr><td align="center" valign="middle" bgcolor="${bgColor}" style="background-color:${bgColor};color:#ffffff;font-weight:800;font-size:11.5pt;mso-line-height-rule:exactly;padding:0;line-height:1;letter-spacing:0.4pt;width:${size};height:${size};font-family:'Inter',Arial,sans-serif;border:none;mso-border-alt:none;">${numStr}</td></tr></table>`;
};

/**
 * Rounded TOC number badge as PNG — Word ignores CSS border-radius on table cells.
 */
export function renderGhgTocNumberBadgeAsImageHtml(num, bgColor) {
  const numStr = String(num).padStart(2, '0');
  const sizePx = parsePtToPx(T.tocBadgeSize);
  const radiusPx = parsePtToPx(T.tocBadgeRadius);
  const cssW = Math.ceil(sizePx);
  const cssH = Math.ceil(sizePx);
  const fontSizePx = parsePtToPx('11.5pt');
  const font = `800 ${fontSizePx}px Arial, Helvetica, sans-serif`;

  if (typeof document === 'undefined') {
    return buildGhgTocNumberBadgeTableHtml(numStr, bgColor);
  }

  try {
    const canvas = document.createElement('canvas');
    const scale = 4;
    canvas.width = Math.max(1, cssW * scale);
    canvas.height = Math.max(1, cssH * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return buildGhgTocNumberBadgeTableHtml(numStr, bgColor);

    ctx.scale(scale, scale);
    drawRoundRectPath(ctx, 0, 0, cssW, cssH, radiusPx);
    ctx.fillStyle = bgColor;
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(numStr, cssW / 2, cssH / 2);

    const dataUrl = canvas.toDataURL('image/png');
    const widthPt = (cssW * 72) / 96;
    const heightPt = (cssH * 72) / 96;

    return `<img class="ghg-toc-number-badge-img" src="${dataUrl}" alt="${numStr}" width="${cssW}" height="${cssH}" vspace="0" hspace="0" style="display:block;width:${widthPt}pt;height:${heightPt}pt;border:0;margin:0;padding:0;mso-wrap-style:none;" />`;
  } catch {
    return buildGhgTocNumberBadgeTableHtml(numStr, bgColor);
  }
}

/** Word-safe numbered TOC badge — PNG in browser export, table fallback in Node/tests. */
export function buildGhgTocNumberBadgeHtml(num, bgColor) {
  const numStr = String(num).padStart(2, '0');
  if (typeof document !== 'undefined') {
    return renderGhgTocNumberBadgeAsImageHtml(num, bgColor);
  }
  return buildGhgTocNumberBadgeTableHtml(numStr, bgColor);
}

const preserveWordTocSubtitleSpaces = (html) => {
  if (html == null || html === '') return '';
  if (String(html).includes('&#160;')) return String(html);
  return String(html).replace(/ /g, '&#160;');
};

const normalizeGhgTocTextParagraphsForWord = (toc) => {
  toc.querySelectorAll('.ghg-toc-text-cell p').forEach((p) => {
    mergeInlineStyle(
      p,
      'width:auto;max-width:100%;word-wrap:break-word;mso-line-height-rule:exactly;',
    );
    const style = (p.getAttribute('style') || '').toLowerCase();
    const isSubtitle =
      p.classList.contains('ghg-toc-subtitle') || /font-size:\s*9pt/i.test(style);
    if (isSubtitle && p.innerHTML && !p.innerHTML.includes('&#160;')) {
      p.innerHTML = preserveWordTocSubtitleSpaces(p.innerHTML);
    }
  });
};

function upgradeGhgTocNumberBadgesForWord(root) {
  root.querySelectorAll('.ghg-toc-badge-table').forEach((table) => {
    const td = table.querySelector('td');
    if (!td || table.parentElement?.querySelector('img.ghg-toc-number-badge-img')) return;
    const numStr = (td.textContent || '').replace(/\s+/g, '').trim();
    if (!numStr) return;
    const bg =
      td.getAttribute('bgcolor') ||
      (td.getAttribute('style') || '').match(/background-color:\s*([^;]+)/i)?.[1]?.trim() ||
      T.primaryBlue;
    const badgeHtml = renderGhgTocNumberBadgeAsImageHtml(
      parseInt(numStr, 10) || numStr,
      bg,
    );
    if (!table.parentElement) return;
    const doc = table.ownerDocument;
    const temp = doc.createElement('div');
    temp.innerHTML = badgeHtml;
    const badge = temp.firstElementChild;
    if (!badge) return;
    const wrap = doc.createElement('span');
    wrap.setAttribute('class', 'ghg-toc-badge-wrap');
    wrap.setAttribute(
      'style',
      'display:block;width:44pt;height:44pt;line-height:0;font-size:0;mso-line-height-rule:exactly;',
    );
    wrap.appendChild(badge);
    table.replaceWith(wrap);
  });

  root.querySelectorAll('.ghg-toc-badge-cell img.ghg-toc-number-badge-img').forEach((img) => {
    if (img.parentElement?.classList.contains('ghg-toc-badge-wrap')) return;
    const doc = img.ownerDocument;
    const wrap = doc.createElement('span');
    wrap.setAttribute('class', 'ghg-toc-badge-wrap');
    wrap.setAttribute(
      'style',
      'display:block;width:44pt;height:44pt;line-height:0;font-size:0;mso-line-height-rule:exactly;',
    );
    img.parentNode.insertBefore(wrap, img);
    wrap.appendChild(img);
  });
}

/**
 * DOM pass: pin TOC grid/row table widths so Word does not float text away from badges.
 * @param {Document|ParentNode} doc
 */
export function applyGhgTocPageDesignForWord(doc) {
  if (!doc?.querySelectorAll) return;

  const gutter = T.tocGridColGutter;
  const badgeGap = T.tocBadgeTextGap;

  doc.querySelectorAll('.ghg-word-toc').forEach((toc) => {
    const shell = toc.querySelector(':scope > div') || toc.firstElementChild;
    if (shell?.setAttribute) {
      const shellCls = (shell.getAttribute('class') || '').split(/\s+/).filter(Boolean);
      if (!shellCls.includes('ghg-toc-page-shell')) {
        shellCls.push('ghg-toc-page-shell');
        shell.setAttribute('class', shellCls.join(' '));
      }
      mergeInlineStyle(
        shell,
        `padding:${T.tocShellPaddingWord};margin:0 auto;width:${T.tocPageWidth};max-width:${T.tocPageWidth};box-sizing:border-box;mso-line-height-rule:exactly;`,
      );
    }

    toc.querySelectorAll('table.ghg-toc-page-layout-table').forEach((layoutTable) => {
      mergeInlineStyle(
        layoutTable,
        `width:100%;border-collapse:collapse;min-height:${T.tocContentLayoutMinHeight};border:none;mso-border-alt:none;`,
      );
      layoutTable.querySelectorAll('.ghg-toc-footer-gap-cell').forEach((cell) => {
        mergeInlineStyle(
          cell,
          `height:${T.tocFooterContentGap};line-height:${T.tocFooterContentGap};font-size:1pt;padding:0;border:none;mso-height-rule:exactly;`,
        );
      });
      layoutTable.querySelectorAll('.ghg-toc-footer-spacer-cell').forEach((cell) => {
        mergeInlineStyle(
          cell,
          `height:${T.tocFooterSpacerHeight};line-height:1pt;font-size:1pt;padding:0;border:none;mso-height-rule:exactly;`,
        );
      });
    });

    const tocRoot = toc;
    if (tocRoot?.setAttribute) {
      mergeInlineStyle(
        tocRoot,
        `width:${T.tocPageWidth};max-width:${T.tocPageWidth};margin:0 auto;background-color:#ffffff;`,
      );
    }

    toc.querySelectorAll('table.ghg-toc-flat-grid').forEach((grid) => {
      const ownerDoc = grid.ownerDocument || toc.ownerDocument;
      grid.setAttribute('width', '100%');
      mergeInlineStyle(
        grid,
        'width:100%;max-width:100%;table-layout:fixed;mso-table-layout-alt:fixed;border-collapse:collapse;',
      );
      let colgroup = grid.querySelector('colgroup');
      if (!colgroup && ownerDoc) {
        colgroup = ownerDoc.createElement('colgroup');
        grid.insertBefore(colgroup, grid.firstChild);
      }
      if (!colgroup) return;
      const colWidths = [
        T.tocBadgeColWidth,
        T.tocTextColWidth,
        T.tocBadgeColWidth,
        T.tocTextColWidth,
      ];
      const colClasses = ['ghg-toc-col-badge', 'ghg-toc-col-text', 'ghg-toc-col-badge', 'ghg-toc-col-text'];
      const existingCols = [...colgroup.querySelectorAll('col')];
      colWidths.forEach((w, i) => {
        let col = existingCols[i];
        if (!col && ownerDoc) {
          col = ownerDoc.createElement('col');
          colgroup.appendChild(col);
        }
        if (!col) return;
        col.setAttribute('class', colClasses[i]);
        col.setAttribute('style', `width:${w};mso-width-source:userset;`);
      });
    });

    toc.querySelectorAll('table.ghg-toc-grid-table > tbody > tr, table.ghg-toc-grid-table > tr').forEach((tr) => {
      let cells = [...tr.children].filter((el) => String(el.tagName).toLowerCase() === 'td');
      if (cells.length === 3) {
        const mid = cells[1];
        const midStyle = (mid.getAttribute('style') || '').toLowerCase();
        const isSpacer =
          !((mid.textContent || '').replace(/\u00a0|\s/g, ''))
          || /width:\s*4%|font-size:\s*1pt/i.test(midStyle);
        if (isSpacer) {
          mergeInlineStyle(cells[0], `padding:0 ${gutter} 0 0;`);
          mergeInlineStyle(cells[2], `padding:0 0 0 ${gutter};`);
          mid.remove();
          cells = [...tr.children].filter((el) => String(el.tagName).toLowerCase() === 'td');
        }
      }
      if (cells.length === 2) {
        cells.forEach((cell, idx) => {
          mergeInlineStyle(
            cell,
            idx === 0
              ? `width:50%;vertical-align:top;padding:0 ${gutter} 0 0;border:none;mso-border-alt:none;`
              : `width:50%;vertical-align:top;padding:0 0 0 ${gutter};border:none;mso-border-alt:none;`,
          );
        });
      }
    });

    toc.querySelectorAll('table.ghg-toc-flat-grid tr.ghg-toc-grid-row, table.ghg-toc-grid-table tr.ghg-toc-grid-row').forEach((row, rowIdx, allRows) => {
      const cells = [...row.children].filter((el) => String(el.tagName).toLowerCase() === 'td');
      const isLastRow = rowIdx === allRows.length - 1;
      const rowPad = isLastRow ? '0' : T.tocItemRowPaddingBottom;
      const noBorder =
        'border:none;border-bottom:none;border-top:none;border-left:none;border-right:none;mso-border-alt:none;mso-border-bottom-alt:none;mso-border-top-alt:none;mso-border-left-alt:none;mso-border-right-alt:none;';
      cells.forEach((cell, idx) => {
        if (cell.classList.contains('ghg-toc-badge-cell')) {
          cell.setAttribute('width', '44');
          mergeInlineStyle(
            cell,
            `width:${T.tocBadgeColWidth};max-width:${T.tocBadgeColWidth};min-width:${T.tocBadgeColWidth};vertical-align:top;padding-top:0;padding-bottom:${rowPad};padding-left:0;padding-right:${badgeGap};${noBorder}`,
          );
          return;
        }
        if (cell.classList.contains('ghg-toc-text-cell')) {
          const padRight = idx === 1 ? gutter : '0';
          mergeInlineStyle(
            cell,
            `width:${T.tocTextColWidth};max-width:${T.tocTextColWidth};vertical-align:top;padding-top:0;padding-bottom:${rowPad};padding-left:0;padding-right:${padRight};word-wrap:break-word;${noBorder}`,
          );
        }
      });
    });

    normalizeGhgTocTextParagraphsForWord(toc);

    toc.querySelectorAll('tr.ghg-toc-grid-row > td.ghg-toc-badge-cell:nth-child(3)').forEach((cell) => {
      mergeInlineStyle(cell, `padding-left:0;padding-right:${badgeGap};border-bottom:none;mso-border-bottom-alt:none;`);
    });

    toc.querySelectorAll('tr.ghg-toc-grid-row > td').forEach((cell) => {
      mergeInlineStyle(cell, 'border-bottom:none;mso-border-bottom-alt:none;');
    });

    const rowPad = T.tocItemRowPaddingBottom;
    toc.querySelectorAll('td.ghg-toc-item-cell').forEach((cell) => {
      const isLeft = cell.classList.contains('ghg-toc-grid-left');
      const isRight = cell.classList.contains('ghg-toc-grid-right');
      const pad =
        isLeft
          ? `0 ${gutter} ${rowPad} 0`
          : isRight
            ? `0 0 ${rowPad} ${gutter}`
            : `0 0 ${rowPad} 0`;
      mergeInlineStyle(
        cell,
        `padding:${pad};vertical-align:top;border:none;mso-border-alt:none;`,
      );
    });

    toc.querySelectorAll('.ghg-toc-badge-wrap').forEach((wrap) => {
      mergeInlineStyle(
        wrap,
        'display:block;width:44pt;height:44pt;line-height:0;font-size:0;mso-line-height-rule:exactly;',
      );
    });

    upgradeGhgTocNumberBadgesForWord(toc);
  });
}

const detectMetaIconTypeFromSrc = (src) => {
  const s = String(src || '');
  if (/M19%203h-1V1|M19 3h-1V1/i.test(s)) return 'calendar';
  if (/M12%2012c2.21|M12 12c2.21/i.test(s)) return 'user';
  return 'doc';
};

const isGhgLabelBadgeCell = (td) => {
  if (!td || String(td.tagName).toLowerCase() !== 'td') return false;
  if (td.querySelector('img')) return false;
  const style = (td.getAttribute('style') || '').toLowerCase();
  if (/height:\s*4pt|line-height:\s*4pt|width:\s*54pt|width:\s*60%|height:\s*3pt/.test(style)) {
    return false;
  }
  if (/width:\s*34pt|height:\s*34pt/.test(style)) return false;
  const text = (td.textContent || '').replace(/\s+/g, ' ').trim();
  if (!text || text.length > 12) return false;
  const bgAttr = (td.getAttribute('bgcolor') || '').toLowerCase();
  const bgStyle = style.match(/background-color:\s*([^;]+)/)?.[1]?.trim().toLowerCase() || '';
  const bg = bgAttr || bgStyle;
  if (!bg || !/(#1d4ed8|#2563eb|rgb\(29,\s*78,\s*216\))/.test(bg)) return false;
  return true;
};

function upgradeGhgCoverMetaIconsForWord(root) {
  root.querySelectorAll('td').forEach((td) => {
    const legacyImg = td.querySelector('img[src*="svg+xml"]');
    const existing = td.querySelector('img.ghg-cover-meta-icon-img');
    if (existing && !legacyImg) return;
    if (!legacyImg && !existing) return;

    const style = (td.getAttribute('style') || '').toLowerCase();
    const isIconCell =
      /width:\s*34pt|height:\s*34pt/.test(style) || legacyImg != null || existing != null;
    if (!isIconCell) return;

    const bg =
      td.getAttribute('bgcolor') ||
      (td.getAttribute('style') || '').match(/background-color:\s*([^;]+)/i)?.[1]?.trim() ||
      T.primaryBlue;
    const iconType =
      existing?.getAttribute('data-ghg-meta-icon') ||
      detectMetaIconTypeFromSrc(legacyImg?.getAttribute('src') || legacyImg?.src);
    const iconHtml = buildGhgCoverMetaIconBoxHtml(iconType, bg);

    const doc = td.ownerDocument;
    const temp = doc.createElement('div');
    temp.innerHTML = iconHtml;
    const iconEl = temp.firstElementChild;
    if (!iconEl) return;

    td.removeAttribute('bgcolor');
    td.innerHTML = '';
    td.appendChild(iconEl);
    mergeInlineStyle(
      td,
      'width:34pt;padding:0;border:none;mso-border-alt:none;vertical-align:middle;text-align:center;',
    );
  });
}

function upgradeGhgLabelBadgesForWord(root) {
  root.querySelectorAll('td').forEach((td) => {
    if (!isGhgLabelBadgeCell(td)) return;
    if (
      td.closest(
        '.ghg-cover-label-badge-img, .ghg-toc-label-badge-img, .ghg-cover-label-badge, .ghg-toc-label-badge',
      )
    ) {
      return;
    }

    const variant = td.closest('.ghg-word-toc') ? 'toc' : 'cover';
    const labelPlain = (td.textContent || '').replace(/\s+/g, ' ').trim();
    const label = escapeHtml(labelPlain);
    const bg =
      td.getAttribute('bgcolor') ||
      (td.getAttribute('style') || '').match(/background-color:\s*([^;]+)/i)?.[1]?.trim() ||
      T.primaryBlue;
    const badgeHtml = renderGhgLabelBadgeAsImageHtml(labelPlain, label, bg, variant);
    const hostTable = td.closest('table');
    if (!hostTable?.parentNode) return;

    const doc = td.ownerDocument;
    const temp = doc.createElement('div');
    temp.innerHTML = badgeHtml;
    const badge = temp.firstElementChild;
    if (badge) hostTable.replaceWith(badge);
  });
}

function upgradeGhgSectionHeadBar(headEl) {
  if (!headEl || headEl.classList.contains('ghg-section-head-bar-pill')) return;
  if (headEl.querySelector('img.ghg-section-head-pill-img')) return;
  const h5 = headEl.querySelector('h5');
  const titlePlain = (h5?.textContent || headEl.textContent || '').replace(/\s+/g, ' ').trim();
  if (!titlePlain) return;

  const pillHtml = renderGhgSectionHeadPillAsImageHtml(titlePlain, T.sectionHeadBarBg, {
    fontSizePt: T.sectionHeadBarPillFontSize,
    padTopPt: T.sectionHeadBarPillPadTop,
    padBottomPt: T.sectionHeadBarPillPadBottom,
    padLeftPt: T.sectionHeadBarPillPadLeft,
    padRightPt: T.sectionHeadBarPillPadRight,
    radiusRight: T.sectionHeadBarPillRadiusRight,
    marginBottomPt: 0,
  });
  const doc = headEl.ownerDocument;
  const temp = doc.createElement('div');
  temp.innerHTML = pillHtml;
  const pill = temp.firstElementChild;
  if (!pill) return;

  headEl.innerHTML = '';
  headEl.appendChild(pill);
  headEl.classList.remove('ghg-section-head-bar');
  headEl.classList.add('ghg-section-head-bar-pill');
}

function applyDarkHighlightWordSafe(root) {
  const highlightBg = `background-color:${T.darkHighlightBg};color:#ffffff;`;
  root.querySelectorAll('.ghg-dark-highlight').forEach((el) => {
    const tag = String(el.tagName || '').toLowerCase();
    if (tag === 'tr' || tag === 'th' || tag === 'td') {
      el.setAttribute('bgcolor', T.darkHighlightBg);
      mergeInlineStyle(el, highlightBg);
    }
    el.querySelectorAll('th, td').forEach((cell) => {
      cell.setAttribute('bgcolor', T.darkHighlightBg);
      mergeInlineStyle(cell, highlightBg);
    });
  });
}

/**
 * DOM upgrades for saved GHG HTML at Word export: PNG badges/icons, table section bars.
 * @param {string} html
 */
export function applyGhgWordExportDesignUpgrades(html) {
  if (!html) return html || '';
  const normalizedInput = String(html).replace(/\bclassName=/g, 'class=');
  if (typeof DOMParser === 'undefined') return normalizedInput;

  const doc = new DOMParser().parseFromString(
    `<div id="ghg-word-root">${normalizedInput}</div>`,
    'text/html',
  );
  const root = doc.getElementById('ghg-word-root');
  if (!root) return normalizedInput;

  upgradeGhgInlineSectionHeadPillsForWord(root);
  root.querySelectorAll('.ghg-section-head-bar').forEach(upgradeGhgSectionHeadBar);
  applyDarkHighlightWordSafe(root);
  upgradeGhgCoverMetaIconsForWord(root);
  upgradeGhgLabelBadgesForWord(root);
  applyGhgTocPageDesignForWord(root);

  return root.innerHTML;
}

/** Modal cover page fragment — badge + meta icons before wrapping for html-docx-js. */
export function prepareGhgModalCoverHtmlForWord(html) {
  return applyGhgWordExportDesignUpgrades(html);
}

export function isGhgWordExportFileName(fileName, options = {}) {
  if (options && options.ghg === true) return true;
  return String(fileName || '').startsWith('GHG_');
}
